// src/components/navbar/MessageDropdown.jsx
import React from "react";
import Dropdown from "components/dropdown";
import { IoChatboxEllipses } from "react-icons/io5";
import { getChatList } from "api";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/authContext";
import { FaUsers } from "react-icons/fa";

// Utility function to construct proper image URL
const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith("http")) {
        return imageUrl;
    }
    
    const serverUrl = process.env.REACT_APP_API_BASE_URL;
    if (serverUrl) {
        return `${serverUrl.replace(/\/$/, "")}/${imageUrl.replace(/^\/uploads/, "uploads/images")}`;
    }
    
    return imageUrl;
};

const MessageDropdown = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: chats } = useQuery({
    queryKey: ['chats', user?.id],
    queryFn: () => getChatList(user.id),
    enabled: !!user,
    refetchInterval: 60000,
  });

  const unreadChats = chats?.filter(chat => chat.unreadCount > 0) || [];
  const hasNewMessages = unreadChats.length > 0;

  const handleMessageClick = (chat) => {
    // FIX: Correctly form the navigation path for both types of chats
    const navPath = chat.type === 'team'
      ? `/admin/inbox?teamChatWith=${chat.id}`
      : `/admin/inbox?chatWith=${chat.id}`;
    navigate(navPath);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Dropdown
      button={
        <div className="relative cursor-pointer">
          <IoChatboxEllipses className={`${hasNewMessages ? "h-6 w-6 text-brand-500" : "h-5 w-5 text-gray-600 dark:text-white"}`} />
          {hasNewMessages && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-xs text-white">
              {unreadChats.length}
            </span>
          )}
        </div>
      }
      children={
        <div className="flex w-[360px] flex-col gap-3 rounded-[20px] bg-white p-4 shadow-xl dark:!bg-navy-700 dark:text-white">
          <div className="flex items-center justify-between">
            <p className="text-xl font-bold text-navy-700 dark:text-white">Messages</p>
          </div>
          
          <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
            {hasNewMessages ? (
              unreadChats.map((chat) => (
                <div
                  key={`${chat.type}-${chat.id}`}
                  onClick={() => handleMessageClick(chat)}
                  className="flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-navy-600"
                >
                  <div className="relative h-12 w-12 flex-shrink-0">
                    {chat.participant?.imageUrl ? (
                      <img 
                        src={getImageUrl(chat.participant.imageUrl)} 
                        alt="avatar" 
                        className="h-full w-full rounded-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null; // Prevent infinite loop
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-200 dark:bg-navy-800" style={{ display: chat.participant?.imageUrl ? 'none' : 'flex' }}>
                      <FaUsers className="h-6 w-6 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-baseline justify-between">
                      <p className="truncate font-bold text-navy-700 dark:text-white">
                        {chat.participant ? `${chat.participant.firstName} ${chat.participant.lastName}` : chat.team.name}
                      </p>
                      <p className="text-xs text-gray-500">{formatTime(chat.lastMessage?.timestamp)}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="truncate text-sm text-gray-500">{chat.lastMessage?.content}</p>
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">
                        {chat.unreadCount}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-4 text-center text-sm text-gray-500">You're all caught up!</p>
            )}
          </div>
          
          <button
            onClick={() => navigate("/admin/inbox")}
            className="mt-2 w-full rounded-lg bg-lightPrimary py-2 text-sm font-bold text-brand-500 transition-colors hover:bg-gray-200 dark:bg-navy-800 dark:hover:bg-navy-600"
          >
            View All Messages
          </button>
        </div>
      }
      classNames={"py-2 top-6 -left-[250px] md:-left-[330px] w-max"}
      animation="origin-[75%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
    />
  );
};

export default MessageDropdown;